import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Pencil, Trash2, Mail, Copy, Loader2, Users, X, RotateCcw, Search,
} from "lucide-react";
import { useOfficerTitles } from "@/hooks/useOfficerTitles";
import { useChapters } from "@/hooks/useChapters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


export default function EmailGroups() {
  const { active: CHAPTERS, inactive: inactiveChapters } = useChapters();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const { all_titles: allTitles } = useOfficerTitles();

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", chapters: [], titles: [], access_chapters: [], access_titles: [], extra: [], excluded: [] });
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");
  const [sendLog, setSendLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const previewTimer = useRef(null);

  const fetchSendLog = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/email-groups/send-log`, auth);
      setSendLog(res.data.entries || []);
    } catch (e) { /* manager-only */ }
    // eslint-disable-next-line
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/email-groups`, auth);
      setGroups(res.data.groups || []);
      setCanManage(!!res.data.can_manage);
      if (res.data.can_manage) {
        fetchSendLog();
        axios.get(`${BACKEND_URL}/api/email-groups-directory`, auth)
          .then((r) => setDirectory(r.data.members || []))
          .catch(() => {});
      }
    } catch (e) {
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line
  }, []);

  // Live preview (debounced) while editing
  useEffect(() => {
    if (!dialogOpen) return;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${BACKEND_URL}/api/email-groups/preview`, {
          filters: { chapters: form.chapters, titles: form.titles },
          extra_member_ids: form.extra,
          excluded_member_ids: form.excluded,
        }, auth);
        setPreview(res.data);
      } catch (e) { /* keep last preview */ }
    }, 350);
    return () => clearTimeout(previewTimer.current);
    // eslint-disable-next-line
  }, [dialogOpen, form.chapters, form.titles, form.extra, form.excluded]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", chapters: [], titles: [], access_chapters: [], access_titles: [], extra: [], excluded: [] });
    setPreview(null);
    setSearch("");
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({
      name: g.name, description: g.description || "",
      chapters: g.filters?.chapters || [], titles: g.filters?.titles || [],
      access_chapters: g.access?.chapters || [], access_titles: g.access?.titles || [],
      extra: g.extra_member_ids || [], excluded: g.excluded_member_ids || [],
    });
    setPreview(null);
    setSearch("");
    setDialogOpen(true);
  };

  const toggleIn = (key, value) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));
  };

  const saveGroup = async () => {
    if (!form.name.trim()) return toast.error("Group name is required");
    setSaving(true);
    const body = {
      name: form.name, description: form.description,
      filters: { chapters: form.chapters, titles: form.titles },
      access: { chapters: form.access_chapters, titles: form.access_titles },
      extra_member_ids: form.extra, excluded_member_ids: form.excluded,
    };
    try {
      if (editing) {
        await axios.patch(`${BACKEND_URL}/api/email-groups/${editing.id}`, body, auth);
        toast.success("Group updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/email-groups`, body, auth);
        toast.success("Group created");
      }
      setDialogOpen(false);
      fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/email-groups/${g.id}`, auth);
      toast.success("Group deleted");
      fetchGroups();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const emailGroup = async (g) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/email-groups/${g.id}/recipients?method=mailto`, auth);
      const { emails, missing_email_count } = res.data;
      if (!emails.length) return toast.error("No members in this group have an email on file");
      if (missing_email_count > 0) {
        toast.info(`${missing_email_count} member(s) have no email on file and will be skipped`);
      }
      window.location.href = `mailto:${emails.join(",")}`;
      if (canManage) fetchSendLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load recipients");
    }
  };

  const copyEmails = async (g) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/email-groups/${g.id}/recipients?method=copy`, auth);
      if (!res.data.emails.length) return toast.error("No emails to copy");
      await navigator.clipboard.writeText(res.data.emails.join(", "));
      toast.success(`Copied ${res.data.emails.length} addresses`);
      if (canManage) fetchSendLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Copy failed");
    }
  };

  const previewIds = new Set((preview?.members || []).map((m) => m.id));
  const searchResults = search.trim()
    ? directory.filter((m) =>
        !previewIds.has(m.id) && !form.excluded.includes(m.id) &&
        `${m.handle} ${m.name}`.toLowerCase().includes(search.toLowerCase())
      ).slice(0, 8)
    : [];

  const chipCls = (active) =>
    `px-3 py-1.5 sm:py-1 rounded-full text-xs font-semibold border transition-colors ${
      active ? "bg-amber-600 border-amber-500 text-white" : "bg-slate-900/60 border-slate-700 text-slate-400 hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="icon" onClick={() => navigate("/")}
            className="border-slate-600 text-slate-300 shrink-0" data-testid="emailgroups-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-500 shrink-0" /> Email Groups
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm">Build groups, then open a mail draft with everyone's address filled in</p>
          </div>
          {canManage && (
            <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto" data-testid="emailgroups-add-btn">
              <Plus className="w-4 h-4 mr-1" /> New Group
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : groups.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-12 text-center text-slate-400" data-testid="emailgroups-empty">
              {canManage
                ? 'No email groups yet. Click "New Group" to build your first one.'
                : "No email groups have been shared with your chapter/title yet."}
            </CardContent>
          </Card>
        ) : (
          groups.map((g, i) => (
            <Card key={g.id} className="bg-slate-800/50 border-slate-700" data-testid={`emailgroup-row-${i}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-semibold">{g.name}</p>
                    {g.description && <p className="text-slate-400 text-sm">{g.description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(g.filters?.chapters || []).map((c) => (
                        <Badge key={c} className={inactiveChapters.includes(c) ? "bg-slate-900/40 text-slate-500 border-slate-700 opacity-70" : "bg-blue-900/50 text-blue-300 border-blue-700"}>{inactiveChapters.includes(c) ? `${c} (inactive)` : c}</Badge>
                      ))}
                      {(g.filters?.titles || []).map((t) => (
                        <Badge key={t} className="bg-purple-900/50 text-purple-300 border-purple-700">{t}</Badge>
                      ))}
                      {(g.extra_member_ids || []).length > 0 && (
                        <Badge className="bg-slate-900/60 text-slate-300 border-slate-600">
                          +{g.extra_member_ids.length} hand-picked
                        </Badge>
                      )}
                      <Badge className="bg-slate-900/60 text-slate-300 border-slate-600">
                        <Users className="w-3 h-3 mr-1" /> {g.member_count} members · {g.email_count} emails
                      </Badge>
                      {canManage && ((g.access?.chapters || []).length > 0 || (g.access?.titles || []).length > 0) && (
                        <Badge className="bg-emerald-900/50 text-emerald-300 border-emerald-700" data-testid={`emailgroup-access-badge-${i}`}>
                          Shared: {[...(g.access?.chapters || []), ...(g.access?.titles || [])].join(", ")}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap items-center shrink-0">
                    <Button size="sm" onClick={() => emailGroup(g)}
                      className="bg-amber-600 hover:bg-amber-700 text-white flex-1 sm:flex-none min-h-[40px]" data-testid={`emailgroup-mailto-${i}`}>
                      <Mail className="w-4 h-4 mr-1" /> Email Group
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyEmails(g)}
                      className="border-slate-600 text-slate-300 min-h-[40px]" data-testid={`emailgroup-copy-${i}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    {canManage && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(g)}
                          className="text-slate-400 hover:text-white min-h-[40px]" data-testid={`emailgroup-edit-${i}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteGroup(g)}
                          className="text-red-400 hover:text-red-300 min-h-[40px]" data-testid={`emailgroup-delete-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}

        {canManage && sendLog.length > 0 && (
          <Card className="bg-slate-800/50 border-slate-700" data-testid="emailgroups-sendlog-card">
            <CardContent className="p-4">
              <button onClick={() => setShowLog(!showLog)}
                className="w-full flex items-center justify-between text-left"
                data-testid="emailgroups-sendlog-toggle">
                <span className="text-white font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-500" /> Send Log
                  <Badge className="bg-slate-900/60 text-slate-300 border-slate-600">{sendLog.length}</Badge>
                </span>
                <span className="text-slate-400 text-sm">{showLog ? "Hide" : "Show"}</span>
              </button>
              {showLog && (
                <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-slate-700/60 border border-slate-700 rounded-lg">
                  {sendLog.map((e, i) => (
                    <div key={e.id || i} className="px-3 py-2 text-sm flex items-center gap-2 flex-wrap" data-testid={`sendlog-entry-${i}`}>
                      <span className="text-white font-medium">{e.sent_by}</span>
                      <span className="text-slate-500 text-xs">{e.chapter} {e.title}</span>
                      <span className="text-slate-400">→ {e.group_name}</span>
                      <Badge className="bg-slate-900/60 text-slate-300 border-slate-600">{e.email_count} emails</Badge>
                      <Badge className={e.method === "copy" ? "bg-blue-900/50 text-blue-300 border-blue-700" : "bg-amber-900/50 text-amber-300 border-amber-700"}>
                        {e.method}
                      </Badge>
                      <span className="text-slate-500 text-xs ml-auto">
                        {e.at ? new Date(e.at).toLocaleString() : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-h-[92vh] overflow-y-auto w-[95vw] max-w-2xl p-4 sm:p-6 rounded-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Group" : "New Group"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Group name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. HA Chapter Officers"
                  className="bg-slate-700 border-slate-600 text-white" data-testid="emailgroup-name-input" />
              </div>
              <div>
                <Label className="text-slate-300">Description (optional)</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white" data-testid="emailgroup-desc-input" />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Chapters filter <span className="text-slate-500 text-xs">(new matching members auto-included; empty = no chapter filter)</span></Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {CHAPTERS.map((c) => (
                  <button key={c} onClick={() => toggleIn("chapters", c)} className={chipCls(form.chapters.includes(c))}
                    data-testid={`emailgroup-chapter-${c}`}>{c}</button>
                ))}
                <button
                  onClick={() => setForm((f) => ({ ...f, chapters: f.chapters.length === CHAPTERS.length ? [] : [...CHAPTERS] }))}
                  className={chipCls(form.chapters.length === CHAPTERS.length)}
                  data-testid="emailgroup-chapter-all">
                  All chapters
                </button>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Titles filter <span className="text-slate-500 text-xs">(empty = all titles)</span></Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {allTitles.map((t) => (
                  <button key={t} onClick={() => toggleIn("titles", t)} className={chipCls(form.titles.includes(t))}
                    data-testid={`emailgroup-title-${t}`}>{t}</button>
                ))}
              </div>
              {form.chapters.length === 0 && form.titles.length === 0 && form.extra.length === 0 && (
                <p className="text-amber-500/80 text-xs mt-1">Tap chapter/title filters above, hit "All chapters", or add members from the list below.</p>
              )}
            </div>

            <div className="border border-emerald-800/50 bg-emerald-900/10 rounded-lg p-3">
              <Label className="text-emerald-300">Who can use this group <span className="text-slate-500 text-xs">(view + email it — leave empty to keep it managers-only)</span></Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {CHAPTERS.map((c) => (
                  <button key={c} onClick={() => toggleIn("access_chapters", c)}
                    className={`px-3 py-1.5 sm:py-1 rounded-full text-xs font-semibold border transition-colors ${
                      form.access_chapters.includes(c)
                        ? "bg-emerald-600 border-emerald-500 text-white"
                        : "bg-slate-900/60 border-slate-700 text-slate-400 hover:text-white"
                    }`}
                    data-testid={`emailgroup-access-chapter-${c}`}>{c}</button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {allTitles.map((t) => (
                  <button key={t} onClick={() => toggleIn("access_titles", t)}
                    className={`px-3 py-1.5 sm:py-1 rounded-full text-xs font-semibold border transition-colors ${
                      form.access_titles.includes(t)
                        ? "bg-emerald-600 border-emerald-500 text-white"
                        : "bg-slate-900/60 border-slate-700 text-slate-400 hover:text-white"
                    }`}
                    data-testid={`emailgroup-access-title-${t}`}>{t}</button>
                ))}
              </div>
              {(form.access_chapters.length > 0 || form.access_titles.length > 0) && (
                <p className="text-emerald-400/80 text-xs mt-1.5">
                  Usable by: {form.access_chapters.length ? form.access_chapters.join("/") : "any chapter"}
                  {" · "}{form.access_titles.length ? form.access_titles.join("/") : "any title"}
                </p>
              )}
            </div>

            <div>
              <Label className="text-slate-300">Hand-pick members</Label>
              <div className="relative mt-1.5">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by handle or name to add…"
                  className="bg-slate-700 border-slate-600 text-white pl-8" data-testid="emailgroup-search-input" />
              </div>
              {searchResults.length > 0 && (
                <div className="mt-1 border border-slate-700 rounded-lg divide-y divide-slate-700/60 bg-slate-900/60">
                  {searchResults.map((m) => (
                    <button key={m.id}
                      onClick={() => { toggleIn("extra", m.id); setSearch(""); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-700/50 text-sm"
                      data-testid={`emailgroup-pick-${m.handle}`}>
                      <Plus className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-white">{m.handle}</span>
                      <span className="text-slate-500 text-xs">{m.chapter} · {m.title}</span>
                      {!m.has_email && <span className="text-amber-500/80 text-[10px] ml-auto">no email</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">
                  {form.chapters.length === 0 && form.titles.length === 0 && form.extra.length === 0 ? (
                    <>All members <span className="text-slate-500 text-xs">({directory.length} in database — tap + to add)</span></>
                  ) : (
                    <>Recipients preview{" "}
                      <span className="text-amber-400 font-semibold" data-testid="emailgroup-preview-count">
                        {preview ? `${preview.member_count} members · ${preview.email_count} emails` : "…"}
                      </span>
                    </>
                  )}
                </Label>
              </div>
              <div className="mt-1.5 max-h-40 sm:max-h-56 lg:max-h-72 overflow-y-auto border border-slate-700 rounded-lg divide-y divide-slate-700/60 bg-slate-900/40">
                {form.chapters.length === 0 && form.titles.length === 0 && form.extra.length === 0 ? (
                  directory.length === 0 ? (
                    <p className="text-slate-500 text-sm px-3 py-3">Loading member database…</p>
                  ) : (
                    directory.map((m) => (
                      <button key={m.id} onClick={() => toggleIn("extra", m.id)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-700/50 text-sm"
                        data-testid={`emailgroup-browse-${m.handle}`}>
                        <Plus className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        <span className="text-white">{m.handle}</span>
                        <span className="text-slate-500 text-xs">{m.chapter} · {m.title}</span>
                        {!m.has_email && <span className="text-amber-500/80 text-[10px] ml-auto">no email</span>}
                      </button>
                    ))
                  )
                ) : (
                <>
                {(preview?.members || []).map((m) => (
                  <div key={m.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                    <span className="text-white">{m.handle}</span>
                    <span className="text-slate-500 text-xs">{m.chapter} · {m.title}</span>
                    {form.extra.includes(m.id) && (
                      <span className="text-[10px] text-blue-400">hand-picked</span>
                    )}
                    {m.email_source === "personal" && (
                      <span className="text-[10px] text-slate-400 border border-slate-600 rounded-full px-1.5">personal</span>
                    )}
                    {!m.has_email && <span className="text-amber-500/80 text-[10px]">no email</span>}
                    <button onClick={() => form.extra.includes(m.id) ? toggleIn("extra", m.id) : toggleIn("excluded", m.id)}
                      className="ml-auto text-slate-500 hover:text-red-400" title="Remove from group"
                      data-testid={`emailgroup-exclude-${m.handle}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {preview && preview.members.length === 0 && (
                  <p className="text-slate-500 text-sm px-3 py-3">No members match these filters.</p>
                )}
                </>
                )}
              </div>
              {form.excluded.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.excluded.map((id) => {
                    const m = directory.find((d) => d.id === id);
                    return (
                      <button key={id} onClick={() => toggleIn("excluded", id)}
                        className="inline-flex items-center gap-1 text-xs bg-red-900/30 border border-red-800 rounded-full px-2 py-0.5 text-red-300 hover:bg-red-900/50"
                        title="Click to restore" data-testid={`emailgroup-restore-${m?.handle || id}`}>
                        <RotateCcw className="w-3 h-3" /> {m?.handle || "member"} excluded
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}
              className="border-slate-600 text-slate-300 w-full sm:w-auto min-h-[44px]">Cancel</Button>
            <Button onClick={saveGroup} disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto min-h-[44px]" data-testid="emailgroup-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save Changes" : "Create Group"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
